import React, { useState, useEffect } from "react";
import { UsersIcon, ExternalLinkIcon } from "lucide-react";
import { Button } from "../ui/button";
import { FriendListItem } from "./FriendListItem";
import { getCurrentFriends, Friend } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";
import { SearchFriendsDialog } from "./SearchFriendsDialog";

interface CurrentFriendsProps {
  refreshTrigger?: number;
}

export const CurrentFriends: React.FC<CurrentFriendsProps> = ({
  refreshTrigger,
}) => {
  const { user } = useAuth();
  const [friends, setFriends] = useState<Friend[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState(false);
  const [dialogReloading, setDialogReloading] = useState(false);

  // Load current friends
  const loadFriends = async (forDialog: boolean = false) => {
    if (!user?.id) return;

    if (forDialog) {
      setDialogReloading(true);
    } else {
      setLoading(true);
    }

    try {
      const result = await getCurrentFriends(user.id);

      if (result.success && result.data) {
        setFriends(result.data);
      } else {
        toast.error(result.error || "Gagal memuat daftar teman");
      }
    } catch (error) {
      console.error("Error loading friends:", error);
      toast.error("Terjadi kesalahan saat memuat daftar teman");
    } finally {
      if (forDialog) {
        setDialogReloading(false);
      } else {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadFriends();
  }, [user?.id]);

  // React to refresh trigger changes
  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      loadFriends();
    }
  }, [refreshTrigger]);

  const handleFriendRemoved = (friendId: string) => {
    // Remove the friend from local state
    setFriends(prev => prev.filter(friend => friend.friend_id !== friendId));
  };

  const handleOpenSearch = () => {
    setIsSearchDialogOpen(true);
  };

  const handleCloseSearch = () => {
    setIsSearchDialogOpen(false);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-[#373e46]">Teman Saya</h3>
        </div>

        {/* Loading skeleton */}
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex items-center gap-3 p-3 animate-pulse">
              <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
              <div className="flex-1">
                <div className="h-3 bg-gray-200 rounded mb-1"></div>
                <div className="h-2 bg-gray-200 rounded w-3/4 mb-1"></div>
                <div className="h-2 bg-gray-200 rounded w-1/2"></div>
              </div>
              <div className="w-8 h-6 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-[#373e46]">Teman Saya</h3>
        <Button
          onClick={handleOpenSearch}
          variant="ghost"
          size="sm"
          className="text-[#6b2cfe] hover:text-[#5a24d4] hover:bg-[#6b2cfe]/10 p-1 h-auto text-xs flex items-center gap-1"
        >
          <span>See All</span>
          <ExternalLinkIcon className="w-3 h-3" />
        </Button>
      </div>

      {/* Friends List */}
      {friends.length > 0 ? (
        <div className="space-y-1">
          {friends.slice(0, 5).map(friend => (
            <FriendListItem
              key={friend.id}
              friend={friend}
              onFriendRemoved={handleFriendRemoved}
            />
          ))}

          {friends.length > 5 && (
            <div className="pt-3 text-center">
              <Button
                onClick={handleOpenSearch}
                variant="outline"
                size="sm"
                className="text-[#6b2cfe] border-[#6b2cfe] hover:bg-[#6b2cfe] hover:text-white h-8 text-xs transition-all duration-300 px-4"
              >
                Lihat {friends.length - 5} Teman Lainnya
              </Button>
            </div>
          )}
        </div>
      ) : (
        /* Empty State */
        <div className="text-center py-8">
          <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-3">
            <UsersIcon className="w-6 h-6 text-gray-400" />
          </div>
          <h4 className="text-sm font-medium text-[#373e46] mb-1">
            Belum Ada Teman
          </h4>
          <p className="text-xs text-gray-500 mb-3">
            Mulai tambahkan teman dari rekomendasi di atas
          </p>
          <Button
            onClick={() => loadFriends(false)}
            size="sm"
            className="bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] hover:opacity-90 text-white px-4 py-1 rounded-lg font-medium transition-all duration-300 hover:shadow-lg text-xs h-7"
          >
            Refresh
          </Button>
        </div>
      )}
      <SearchFriendsDialog
        isOpen={isSearchDialogOpen}
        onClose={handleCloseSearch}
        friends={friends}
        isLoading={dialogReloading}
        onRefresh={() => loadFriends(true)}
      />
    </div>
  );
};
